import styles from "../styles/login.module.scss"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo } from "react"
import logo from "../assets/appIcon.png"
import ShuffleFill from "~icons/eva/shuffle-2-fill"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { LoginPageStore } from "../stores/LoginPageStore"
import { useSignIn } from "../hooks/useSignIn"
import { load, save } from "../utils/storage"
import Config from "../config"
import { ErrorHandledImage } from "../components/atoms/ErrorHandledImage"
import LoginImage from "../assets/loginImage.png"
import classNames from "classnames"
import { ConfirmationCodeInput } from "../components/molecules/ConfirmationCodeInput"
import { getAgentProfile } from "../services/firebase-functions/agentProfile"
import { useQuery } from "@tanstack/react-query"
import { getAuth } from "firebase/auth"
import { DefaultInput } from "../components/atoms/DefaultInput"
import { colors } from "@theme/colors"

const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const LoginPage = observer(function LoginPage() {
  const store = useMemo(() => new LoginPageStore(), [])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateSendLoginLink, onCodeEntered, isPending, loggedInUser } = useSignIn()
  const currentUser = getAuth().currentUser
  const { data: agentProfile, isLoading: isAgentProfileLoading } = useQuery({
    queryKey: ["agentProfile", currentUser?.uid],
    queryFn: () => getAgentProfile(currentUser?.uid || ""),
  })

  useEffect(() => {
    const fbclid = searchParams.get("fbclid")
    if (fbclid) {
      save("fbClickData", {
        fbclid,
        fbclidCreatedAt: new Date().toISOString(),
      })
    }
  }, [])

  useEffect(() => {
    const doesAgentProfileExist = !isAgentProfileLoading && agentProfile

    if (loggedInUser && doesAgentProfileExist) {
      navigate("/dashboard")
    }
  }, [loggedInUser, isAgentProfileLoading])

  useEffect(() => {
    const user = load<{ email: string }>("user")
    if (user?.email) {
      store.setEmail(user.email)
    }
  }, [])

  const loginWithCode = async () => {
    await onCodeEntered(store.email, store.confirmationCode)
    store.setConfirmationCode("")
  }

  useEffect(() => {
    if (store.confirmationCode.length >= 6) {
      loginWithCode()
    }
  }, [store.confirmationCode])

  const isValidEmail = emailRegExp.test(store.email)
  const isConfirmationCodeEntered = store.confirmationCode.length >= 6
  const isEmailEntered =
    store.isCodeInputVisible || isPending || Config.environment !== "development"

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <Link to="/">
              <img alt="cruisen logo" src={logo} className={styles.logoImage} />
            </Link>
            <h2 className={styles.title}>Welcome, Captain!</h2>
            <p className={styles.description}>
              {store.isCodeInputVisible
                ? "Please enter the confirmation code you received at your email address. It may take a few minutes to arrive."
                : "Enter your email address to continue."}
            </p>
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault()
              }}
            >
              <div className={styles.loginInputContainer}>
                <DefaultInput
                  name="email"
                  type="email"
                  label="Enter your email"
                  className={classNames(!isEmailEntered && styles.loginInput)}
                  onChange={(value) => store.setEmail(value)}
                  value={store.email}
                  disabled={store.isCodeInputVisible}
                  testId="email-input"
                  parentBackgroundColor={
                    store.isCodeInputVisible
                      ? colors.backgroundTertiary
                      : colors.backgroundSecondary
                  }
                />
                {isEmailEntered ? null : (
                  <button
                    className={styles.generateEmailButton}
                    type="button"
                    onClick={() => {
                      store.setEmail(
                        `test-account-${Math.floor(Math.random() * 1_000_000_000)}@cruisen.com`,
                      )
                    }}
                  >
                    <ShuffleFill />
                  </button>
                )}
              </div>
              {store.isCodeInputVisible ? (
                <ConfirmationCodeInput
                  codeLength={6}
                  onCodeEntered={(code) => store.setConfirmationCode(code)}
                />
              ) : null}
              {store.isCodeInputVisible ? (
                <div className={styles.buttonGroup}>
                  <button
                    type="button"
                    onClick={() => mutateSendLoginLink(store.email)}
                    className={styles.generateButton}
                    data-testid="resend-code-button"
                    disabled={isPending}
                  >
                    {isPending
                      ? isConfirmationCodeEntered
                        ? "Loading..."
                        : "Resending..."
                      : "Resend code"}
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.buttonGroup}>
                    <button
                      type="submit"
                      onClick={async () => {
                        store.setPressedButton("signin")
                        const result = await mutateSendLoginLink(store.email)
                        if (result?.showCodeInput) {
                          store.setIsCodeInputVisible(true)
                        }
                      }}
                      className={styles.button}
                      data-testid="login-button"
                      disabled={isPending || !isValidEmail}
                    >
                      {isPending ? "Loading..." : "Continue"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
          <div className={styles.imageSection}>
            <ErrorHandledImage
              className={styles.sectionImage}
              uri={LoginImage}
              alt="Cruise sailing image"
            />
          </div>
        </div>
      </div>
      {!Config.isProduction && (
        <div className={styles.versionInfo}>{`${Config.environment} v${Config.appVersion}`}</div>
      )}
    </>
  )
})

export default LoginPage
